import { Input } from '@/components/ui/input';
import { useClassifierConfigList } from '@/hooks/useClassifierConfigs';

import type { RunClassifierNodeData } from '@repo/shared-types';

interface Props {
  data: RunClassifierNodeData;
  onChange: (data: RunClassifierNodeData) => void;
}

export const RunClassifierConfig = ({ data, onChange }: Props) => {
  const { data: configs } = useClassifierConfigList();
  const update = (patch: Partial<RunClassifierNodeData>) => onChange({ ...data, ...patch });

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Label</label>
        <Input
          value={data.label}
          onChange={(e) => update({ label: e.target.value })}
          placeholder="Classifier"
        />
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Classifier Config</label>
        <select
          value={data.classifierConfigId ?? ''}
          onChange={(e) => update({ classifierConfigId: e.target.value || undefined })}
          className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
        >
          <option value="">(Use default for this DB)</option>
          {(configs ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} {c.is_default ? '(default)' : ''}
            </option>
          ))}
        </select>
        <p className="text-muted-foreground text-[10px]">
          Leave empty to use whichever classifier_configs row has is_default=true.
        </p>
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Output Key</label>
        <Input
          value={data.outputKey ?? 'classification'}
          onChange={(e) => update({ outputKey: e.target.value })}
          placeholder="classification"
          className="font-mono text-xs"
        />
        <p className="text-muted-foreground text-[10px]">
          Templates can read {`{{<outputKey>.labels.<key>}}`} or {`{{<outputKey>.data.<key>}}`}.
        </p>
      </div>

      <label className="flex items-start gap-2 text-xs">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={data.sessionOnly ?? false}
          onChange={(e) => update({ sessionOnly: e.target.checked })}
        />
        <span>
          This session only
          <span className="text-muted-foreground block text-[10px]">
            WhatsApp, web chat and email only. Off, the agent also sees the earlier messages in
            the thread. On, it sees this session and nothing before it. Voice calls are
            unaffected.
          </span>
        </span>
      </label>
    </div>
  );
};
